import { Box, Stack, styled, TextField, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import queryString from 'query-string';
import React from 'react';
import { AlertCircle, Download } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import ModalComponent from 'src/components/Modal/ModalComponent';

// Utilities
import { isUndefined } from 'lodash';
import IconCredit from 'src/asset/icons/icon-credit.svg';
import { CreditCoinIcon } from 'src/components/Icons';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useGetRemainCredit } from 'src/hooks/useGetPointsFeature';
import { useQueryParams } from 'src/hooks/useQueryParam';
import useRefetch from 'src/hooks/useRefetch';
import { useMutateExportBOLsList, useMutateExportBOLsListByCompany } from 'src/services/mutate';
import { QUERY_KEY_USAGE } from 'src/services/queries';
import { MAXIMUM_RECORDS_EXPORT_BILLS_OF_LADING } from 'src/utils/constants/config';
import { FilterWithheld, KeyFilterChartTradeDataUnitBy } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getFormItemError, renderFormItemError } from 'src/utils/helpers/renderFormItemError';
import { renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';
import { getPageSizeBaseOnTotalRecords } from 'src/utils/helpers/utilities';
import { NotifyType, useNotifyCreditCosts } from 'src/zustand/useNotifyCreditCosts';
import useSnackbarStore from 'src/zustand/useSnackbarStore';
import { FeatureAction } from 'src/utils/constants/featuresPoint';

interface IProps {
  open: boolean;
  handleClose: () => void;
  totalRecordsAvailable: number;
  type?: 'hscode' | 'company';
  sortBy?: KeyFilterChartTradeDataUnitBy;
  order?: 'asc' | 'desc';
}

const StyledHeaderAlert = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.success.light,
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  padding: '8px 32px',
}));

const PopupExportBOLs: React.FC<IProps> = ({
  open,
  handleClose,
  totalRecordsAvailable,
  type = 'hscode',
  sortBy = KeyFilterChartTradeDataUnitBy.value_usd,
  order = 'desc',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryTradeData = useQueryParams();
  const { handleOpenNotify } = useNotifyCreditCosts((state) => state);
  const { mutateAsync: hscodeMutateAsync, isPending: hscodeMutatePending } = useMutateExportBOLsList();
  const { mutateAsync: companyMutateAsync, isPending: companyMutatePending } = useMutateExportBOLsListByCompany();
  const { getFeaturePoints } = useFeatureAccess();
  const pointsDownloadBOLs = getFeaturePoints(URL_ENDPOINT.companyTradeData.exportListBOLs, FeatureAction.VIEW);
  const remainCredit = useGetRemainCredit();
  const { refetchQuery } = useRefetch();
  const { dispatch: dispatchSnackbar } = useSnackbarStore();
  const formik = useFormik<ExpectedAny>({
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: {
      from_record: 1,
      to_record: 10,
    },
    validationSchema: Yup.object().shape({
      from_record: Yup.number().required('This field is required').min(1, 'From must be at least 1'),
      to_record: Yup.number()
        .required('This field is required')
        .min(1, 'To must be at least 1')
        .max(totalRecordsAvailable, `We just have ${renderNumberOrDefault(totalRecordsAvailable)} records available`)
        .test('to_record_valid', 'Must be greater than or equal to From', (text?: number, context?: ExpectedAny) => {
          const recordFrom = context?.parent?.from_record || 1;
          const recordTo = text || 1;
          return recordFrom <= recordTo;
        })
        .test(
          'total_record_limit',
          `You can only export a maximum of ${renderNumberOrDefault(MAXIMUM_RECORDS_EXPORT_BILLS_OF_LADING)} at a time`,
          (text?: number, context?: ExpectedAny) => {
            const recordFrom = context?.parent?.from_record || 1;
            const recordTo = text || 0;
            return recordTo - recordFrom + 1 <= MAXIMUM_RECORDS_EXPORT_BILLS_OF_LADING;
          },
        ),
    }),
    onSubmit: async (values) => {
      const totalRecords = values.to_record - values.from_record + 1;
      let response;
      const payload = queryString.stringify({
        ...queryTradeData,
        hide_withheld_government: queryTradeData?.hide_withheld_government || FilterWithheld.NOT_ALLOWED,
        from_record: values.from_record,
        to_record: values.to_record,
        page_size: getPageSizeBaseOnTotalRecords(totalRecords),
        sort_by: sortBy,
        order: order,
      });

      if (type === 'hscode') {
        response = await hscodeMutateAsync(payload);
      } else {
        response = await companyMutateAsync(payload);
      }

      if (response.status !== SUCCESS_CODE.GET) {
        dispatchSnackbar({
          open: true,
          title: 'Error',
          description: 'Export fail. Please try again.',
          variant: 'standard',
          color: 'error',
        });
        return;
      }

      // Tạo URL từ `Blob`
      const url = URL.createObjectURL(response?.data);

      // Tạo một thẻ `<a>` tạm thời để kích hoạt tải file
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bills_of_lading_data.xlsx'; // Tên file CSV được tải về

      // Thêm thẻ `a` vào DOM và kích hoạt sự kiện click
      document.body.appendChild(a);
      a.click();

      // Sau khi tải xong, xóa thẻ `a` và URL object
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      handleClose();
      refetchQuery([QUERY_KEY_USAGE]);
    },
  });

  const handleExport = () => {
    if (isUndefined(pointsDownloadBOLs?.planName) || getTotalCredits() > remainCredit) {
      handleOpenNotify({
        type: NotifyType.NotEnoughCredit,
        showCloseIcon: true,
      });
      return;
    }

    formik.submitForm();
  };

  const getTotalCredits = () => {
    if (formik.values.to_record >= formik.values.from_record) {
      return (formik.values.to_record - formik.values.from_record + 1) * pointsDownloadBOLs?.creditCosts;
    }
    return 0;
  };

  const getTotalRecord = () => {
    if (formik.values.to_record && formik.values.from_record && formik.values.to_record >= formik.values.from_record) {
      return formik.values.to_record - formik.values.from_record + 1;
    }
    return 0;
  };

  return (
    <ModalComponent
      padding={4}
      pt={6}
      handleClose={handleClose}
      open={open}
      closeOnBackdropClick={false}
      disabledCloseIcon={formik.isSubmitting}
      showCloseIcon={true}
      headerAlert={
        pointsDownloadBOLs?.planName ? (
          <StyledHeaderAlert
            id="form-contribute-header-alert"
            direction="row"
            alignItems="center"
            bgcolor={theme.palette.info.light}
          >
            <IconFeather
              icon={<AlertCircle />}
              muiProps={{
                color: 'info',
                fontSize: 'small',
              }}
            />
            <Typography variant="caption2" color="info.main" ml={1}>
              The export action can take up to 1 minute, thank you for your patience.
            </Typography>
          </StyledHeaderAlert>
        ) : (
          <StyledHeaderAlert
            id="form-contribute-header-alert"
            direction="row"
            alignItems="center"
            bgcolor={theme.palette.success.light}
          >
            <Box src={IconCredit} component="img" width={24} />
            <Typography variant="caption2" ml={1}>
              You do not have credits yet.{' '}
              <Typography
                variant="caption2"
                component="span"
                color="secondary.main"
                fontWeight={600}
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(AuthRouteKeys.Pricing)}
              >
                Please choose a suitable Plan.
              </Typography>
            </Typography>
          </StyledHeaderAlert>
        )
      }
    >
      <FormikForm formik={formik}>
        <Stack spacing={1} width={540}>
          <Typography textAlign="center" variant="h5">
            Export Option
          </Typography>
          <Typography textAlign="center" color={'text.secondary'} variant="body2">
            You can export a maximum of {renderNumberOrDefault(MAXIMUM_RECORDS_EXPORT_BILLS_OF_LADING)} records at a
            time.
          </Typography>
          <Box px={2} py={1} bgcolor={theme.palette.background.paper1} borderRadius={theme.shape.borderRadius / 2}>
            <Typography textAlign="left" variant="subtitle2">
              Available records: {renderNumberOrDefault(totalRecordsAvailable)}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="column" spacing={2} mt={4}>
          <Stack direction="row" spacing={2}>
            <Box width="100%">
              <TextField
                value={formik.values.from_record}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(getFormItemError('from_record', formik))}
                id="from_record"
                name="from_record"
                size="small"
                placeholder="eg: 1, 10, 200, ..."
                label="From"
                type="number"
                required
                fullWidth
              />
              {renderFormItemError(getFormItemError('from_record', formik))}
            </Box>
            <Box width="100%">
              <TextField
                value={formik.values.to_record}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(getFormItemError('to_record', formik))}
                id="to_record"
                name="to_record"
                size="small"
                placeholder="eg: 10, 55, 100..."
                label="To"
                type="number"
                required
                fullWidth
              />
              {renderFormItemError(getFormItemError('to_record', formik))}
            </Box>
          </Stack>
          <Box>
            <Typography variant="subtitle2" align="center">
              Total Records To Export
            </Typography>
            <Typography variant="h4" align="center" color="primary.main" mt={1}>
              {getTotalRecord()}
            </Typography>
          </Box>
          <ButtonWithLoading
            variant="contained"
            color="primary"
            size="large"
            isRounded
            onClick={handleExport}
            disabled={hscodeMutatePending || companyMutatePending || !formik.isValid}
            isLoading={hscodeMutatePending || companyMutatePending}
            endIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Download />} />}
            startIcon={
              pointsDownloadBOLs?.planName ? (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={0.5}
                  py={0.5}
                  px={1}
                  borderRadius={theme.shape.borderRadius}
                  bgcolor="rgba(255, 255, 255, 0.12)"
                >
                  <Typography variant="subtitle2" component="span" color="white">
                    {getTotalCredits()}
                  </Typography>
                  <CreditCoinIcon fontSize="small" />
                </Stack>
              ) : (
                <CreditCoinIcon fontSize="small" />
              )
            }
          >
            EXPORT TO CSV
          </ButtonWithLoading>
        </Stack>
      </FormikForm>
    </ModalComponent>
  );
};
export default PopupExportBOLs;
